/**
 * Template Personalizado 1 - Formulario Multi-Página por Secciones.
 *
 * Replica el diseño exacto del mockup: banner superior, dos columnas
 * (info evento a la izquierda, formulario/políticas a la derecha),
 * navegación multi-página por secciones.
 *
 * @author Kiro AI Assistant
 * @date [Fecha de creación: 2025-08-20]
 * @updated [Fecha de última modificación: 2026-09-24]
 * @updatedBy Eulicer.zapata@iudigital.edu.co
 */

import React, { useState, useMemo } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Link,
  Fade,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";

const colors = {
  primary: "#C5292B",
  secondary: "#00416A",
  secondaryDark: "#002b45",
  accent: "#904DA6",
  background: "#ffffff",
  white: "#ffffff",
  success: "#2E7D32",
  grey: "#f5f5f5",
  purple: "#342255",
};

// Detecta si un texto corresponde a "políticas" de forma tolerante:
// normaliza a minúsculas y elimina acentos, de modo que "política",
// "politica", "Politica", "POLÍTICAS", etc. se reconozcan por igual.
const matchesPolitica = (str) =>
  (str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .includes("politica");

export default function CustomTemplate1({
  formData,
  errors,
  loading,
  submitting,
  eventInfo,
  error,
  success,
  registeredData,
  handleInputChange,
  handleSubmit,
  isFormClosed,
  getFormClosureInfo,
  hasAvailableSlots,
  clearError,
  handleCustomFieldChange,
  resetForm,
}) {
  const [currentPage, setCurrentPage] = useState(0);
  const [policyError, setPolicyError] = useState("");
  const bloodTypes = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

  // Detecta móvil real (< md = 900px). En móvil se usan las imágenes de
  // /images/responsive (una por sección). Tablet (md a lg) y PC (lg+) no
  // cambian: siguen con las imágenes originales de escritorio.
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Vuelve a la primera sección (políticas) con todos los campos vacíos.
  const handleGoToStart = () => {
    if (resetForm) resetForm();
    setPolicyError("");
    setCurrentPage(0);
    window.scrollTo(0, 0);
  };

  const pages = useMemo(() => {
    const result = [];
    const customFields = eventInfo?.custom_fields || [];

    // Buscar sección de políticas por nombre (tolerante a acentos).
    const politicasSection = customFields.find(
      (item) => item.fields && item.name && matchesPolitica(item.name)
    );

    // Página 1: Políticas
    if (politicasSection) {
      result.push({ type: "section", title: politicasSection.name, data: politicasSection });
    }

    // Secciones de campos personalizados (excluyendo políticas)
    const contentSections = customFields.filter(
      (item) =>
        item.fields &&
        Array.isArray(item.fields) &&
        !(politicasSection && item.id === politicasSection.id)
    );

    if (contentSections.length > 0) {
      // La primera sección incluye además los campos base del formulario
      contentSections.forEach((item, idx) => {
        result.push({
          type: "section",
          title: item.name,
          data: item,
          includeBaseFields: idx === 0,
        });
      });
    } else {
      // Sin secciones: página dedicada solo a los campos base
      result.push({ type: "base_fields", title: "Datos Personales" });
    }

    // Campos sueltos
    const looseFields = customFields.filter((item) => item.id && item.type && !item.fields);
    if (looseFields.length > 0) {
      result.push({ type: "loose_fields", title: "Información Adicional", data: looseFields });
    }

    return result;
  }, [eventInfo]);

  const totalPages = pages.length;

  // Determina si una página es la de políticas (no se numera como sección).
  const isPoliticasPage = (page) => page?.type === "section" && matchesPolitica(page.title);

  // Devuelve el título de la página con el prefijo "Sección N:" cuando aplica.
  // La página de políticas no se numera; el resto se numeran secuencialmente
  // empezando en 1 (Datos personales = Sección 1, siguiente = Sección 2, ...).
  const getSectionTitle = (page, pageIndex = currentPage) => {
    const title = page?.title || "";
    if (!page || isPoliticasPage(page)) return title;

    const sectionNumber = pages.slice(0, pageIndex + 1).filter((p) => !isPoliticasPage(p)).length;

    // El prefijo "Sección N:" va en negrilla; el nombre en peso normal.
    return (
      <>
        <Box component="span" sx={{ fontWeight: 700 }}>{`Sección ${sectionNumber}: `}</Box>
        <Box component="span" sx={{ fontWeight: 400 }}>
          {title}
        </Box>
      </>
    );
  };

  // Solo tamaño/tipografía para los títulos de sección (ej: "Datos personales").
  // No cambia alineación ni ubicación; conserva los estilos existentes.
  const sectionTitleStyles = {
    fontFamily: "'Roboto Condensed', sans-serif",
    fontWeight: 500,
    fontSize: "20px",
    lineHeight: "30px",
    letterSpacing: "-0.02em",
  };

  const fieldStyles = {
    "& .MuiOutlinedInput-root": {
      minHeight: "42px",
      // Fondo transparente en TODOS los elementos internos del campo, para que
      // donde el campo se solape con la imagen de fondo se vea la imagen a
      // través del campo y no un bloque blanco (evita la sensación de recorte).
      backgroundColor: "transparent",
      "& fieldset": { borderColor: "#ccc" },
      "&:hover fieldset": { borderColor: colors.secondary },
      "&.Mui-focused fieldset": { borderColor: colors.secondary },
    },
    "& .MuiOutlinedInput-input": { backgroundColor: "transparent" },
    "& .MuiSelect-select": { backgroundColor: "transparent" },
    "& input:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 1000px transparent inset",
      transition: "background-color 9999s ease-in-out 0s",
    },
    "& .MuiInputLabel-root.Mui-focused": { color: colors.secondary },
  };

  // Devuelve las opciones que aún se pueden mostrar para un campo, filtrando
  // las que alcanzaron su cupo. El backend expone eventInfo.option_limits como
  // un mapa keyed por el ID del campo: { limit, counts, full: [opciones_llenas] }.
  // Solo trae límites para los campos de la sección "Mesas simultáneas de
  // construcción de recomendaciones"; el resto de campos no aparecen en el mapa
  // y por tanto muestran todas sus opciones.
  const getVisibleOptions = (customField) => {
    const allOptions = Array.isArray(customField.options) ? customField.options : [];
    const limits = eventInfo?.option_limits?.[customField.id];
    if (!limits || !Array.isArray(limits.full) || limits.full.length === 0) {
      return allOptions;
    }
    const label = customField.label || customField.name || customField.id;
    const currentValues = Array.isArray(formData.custom_fields[label])
      ? formData.custom_fields[label]
      : [];
    // Oculta las opciones llenas, salvo que el usuario ya la tenga seleccionada
    // en esta sesión (para no romper una selección en curso).
    return allOptions.filter(
      (option) => !limits.full.includes(option) || currentValues.includes(option)
    );
  };

  const renderCustomFieldInput = (
    customField,
    fullWidthColumn = false,
    twoColChecklist = false,
    gridSx = null
  ) => {
    const fieldLabel = customField.label || customField.name || customField.id;
    const displayLabel = customField.required
      ? `${customField.label} `
      : `${customField.label} (Opcional)`;
    // Ancho del campo dentro del Grid: media columna por defecto,
    // o columna completa cuando se usa el layout de 3 columnas.
    const fieldColSpan = fullWidthColumn ? 12 : 6;

    if (customField.type === "checklist") {
      // En la página de políticas los checks van en fila (Acepto / No acepto);
      // en las demás secciones se apilan uno debajo del otro.
      const isPolicyPage = currentPage === 0;
      // Evita duplicar el label cuando coincide con el título de la sección.
      const sectionTitle = currentPageData?.title || "";
      const labelText = (customField.label || "").trim();
      const showChecklistLabel = !isPolicyPage && labelText && labelText !== sectionTitle.trim();

      // Opciones que aún tienen cupo. Si el campo tiene límites configurados y
      // todas sus opciones ya están llenas, no queda ninguna por mostrar: en
      // ese caso, en lugar de dejar un espacio en blanco, se informa al usuario
      // que ya no hay cupo pero que puede continuar con el registro.
      const visibleOptions = getVisibleOptions(customField);
      const hasLimits = Array.isArray(eventInfo?.option_limits?.[customField.id]?.full);
      const allOptionsFull = hasLimits && visibleOptions.length === 0;

      if (allOptionsFull) {
        return (
          <Grid item xs={12} key={customField.id}>
            {showChecklistLabel && (
              <Typography sx={{ fontSize: "0.9rem", color: "#1a1a1a", mb: 1.5, fontWeight: 500 }}>
                {displayLabel}
              </Typography>
            )}
            <Alert
              severity="info"
              sx={{
                fontFamily: "'Roboto Condensed', sans-serif",
                fontSize: "20px",
                lineHeight: "28px",
                alignItems: "center",
                "& .MuiAlert-message": { fontSize: "20px", lineHeight: "28px" },
                "& .MuiAlert-icon": { fontSize: "26px" },
              }}
            >
              Los cupos para las mesas de trabajo se han completado. Puedes continuar con tu
              registro para participar en las demás actividades programadas del evento.
            </Alert>
          </Grid>
        );
      }

      return (
        <Grid item xs={12} key={customField.id}>
          {showChecklistLabel && (
            <Typography sx={{ fontSize: "0.9rem", color: "#1a1a1a", mb: 1.5, fontWeight: 500 }}>
              {displayLabel}
            </Typography>
          )}
          <Box
            sx={{
              display: isPolicyPage ? "flex" : "grid",
              ...(isPolicyPage
                ? { flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: 2 }
                : {
                    gridTemplateColumns: twoColChecklist ? "1fr 1fr" : "1fr",
                    columnGap: 2,
                    rowGap: 0.5,
                  }),
              minHeight: "42px",
            }}
          >
            {visibleOptions.map((option) => {
              const currentValues = Array.isArray(formData.custom_fields[fieldLabel])
                ? formData.custom_fields[fieldLabel]
                : [];
              return (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      checked={currentValues.includes(option)}
                      onChange={(e) => {
                        // Sección de políticas y Sección 2 (mesas): solo permite una selección.
                        const singleSelect = isPolicyPage || currentStepIndex === 1;
                        const newValues = singleSelect
                          ? e.target.checked
                            ? [option]
                            : []
                          : e.target.checked
                          ? [...currentValues, option]
                          : currentValues.filter((v) => v !== option);
                        handleCustomFieldChange(customField.id, newValues);
                        if (policyError) setPolicyError("");
                      }}
                      disabled={submitting}
                      size="small"
                      sx={{ p: "4px", mr: 1, "&.Mui-checked": { color: colors.secondary } }}
                    />
                  }
                  label={option}
                  sx={{
                    m: 0,
                    py: 0.3,
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    "& .MuiFormControlLabel-label": {
                      fontFamily: "'Roboto Condensed', sans-serif !important",
                      fontWeight: "300 !important",
                      fontStyle: "normal",
                      fontSize: "20px !important",
                      lineHeight: "30px !important",
                      letterSpacing: "-0.02em",
                      color: "#333 !important",
                      mt: "3px",
                      flex: 1,
                    },
                  }}
                />
              );
            })}
          </Box>
          {errors[`custom_${customField.id}`] && (
            <FormHelperText error sx={{ ml: "14px", mt: "3px" }}>
              {errors[`custom_${customField.id}`]}
            </FormHelperText>
          )}
        </Grid>
      );
    }

    if (customField.type === "select") {
      return (
        <Grid item xs={12} md={fieldColSpan} key={customField.id} sx={gridSx || undefined}>
          <TextField
            fullWidth
            select
            required={customField.required}
            label={displayLabel}
            value={formData.custom_fields[fieldLabel] || ""}
            onChange={(e) => handleCustomFieldChange(customField.id, e.target.value)}
            disabled={submitting}
            error={!!errors[`custom_${customField.id}`]}
            helperText={errors[`custom_${customField.id}`]}
            sx={fieldStyles}
          >
            <MenuItem value="">
              <em>Seleccionar...</em>
            </MenuItem>
            {getVisibleOptions(customField).map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      );
    }

    return (
      <Grid item xs={12} md={fieldColSpan} key={customField.id} sx={gridSx || undefined}>
        <TextField
          fullWidth
          required={customField.required}
          label={displayLabel}
          value={formData.custom_fields[fieldLabel] || ""}
          onChange={(e) => handleCustomFieldChange(customField.id, e.target.value)}
          disabled={submitting}
          error={!!errors[`custom_${customField.id}`]}
          helperText={errors[`custom_${customField.id}`]}
          sx={fieldStyles}
        />
      </Grid>
    );
  };

  // Verifica si el usuario aceptó la política de tratamiento de datos.
  // Regla: si en alguna checklist de la sección de políticas seleccionó una
  // opción de rechazo ("No acepto") o no seleccionó una opción de aceptación
  // ("Acepto"), no puede continuar.
  const isPolicyAccepted = (pageData) => {
    if (!pageData?.data?.fields) return true;

    const checklists = pageData.data.fields.filter((f) => f.type === "checklist");
    if (checklists.length === 0) return true;

    return checklists.every((field) => {
      const fieldLabel = field.label || field.name || field.id;
      const values = Array.isArray(formData.custom_fields[fieldLabel])
        ? formData.custom_fields[fieldLabel]
        : [];

      const hasReject = values.some((v) => v.toLowerCase().includes("no acepto"));
      const hasAccept = values.some(
        (v) => v.toLowerCase().includes("acepto") && !v.toLowerCase().includes("no acepto")
      );

      return hasAccept && !hasReject;
    });
  };

  // Comprueba si un valor de campo está vacío (soporta strings y arrays de checklist).
  const isEmptyValue = (value) => {
    if (value === undefined || value === null) return true;
    if (Array.isArray(value)) return value.length === 0;
    return String(value).trim() === "";
  };

  // Valida los campos obligatorios de la página actual (campos base + personalizados).
  // Devuelve true si están todos completos.
  const validateRequiredFields = (pageData) => {
    if (!pageData) return true;

    // Campos base obligatorios (solo en la sección que los incluye o en base_fields).
    const requiresBase =
      pageData.type === "base_fields" ||
      (pageData.type === "section" && pageData.includeBaseFields);

    if (requiresBase) {
      const baseRequired = ["name", "lastname", "number_id", "email", "blood_type"];
      const missingBase = baseRequired.some((f) => isEmptyValue(formData[f]));
      if (missingBase) return false;
    }

    // Campos personalizados obligatorios de la sección.
    const fields = pageData.data?.fields || (pageData.type === "loose_fields" ? pageData.data : []);
    if (Array.isArray(fields)) {
      const missingCustom = fields.some((field) => {
        const fieldLabel = field.label || field.name || field.id;
        const value = formData.custom_fields[fieldLabel];

        // Si el campo tiene límites de cupo y todas sus opciones ya están
        // llenas, no queda nada por seleccionar: no se puede exigir selección,
        // así que se considera válido para permitir continuar el registro.
        if (field.type === "checklist") {
          const hasLimits = Array.isArray(eventInfo?.option_limits?.[field.id]?.full);
          if (hasLimits && getVisibleOptions(field).length === 0) return false;
        }

        // Regla especial: si una opción de checklist dice "(Obligatorio)",
        // esa opción debe estar marcada obligatoriamente.
        // También, si la sección es de "Consentimientos", TODAS las opciones son obligatorias.
        if (field.type === "checklist" && Array.isArray(field.options)) {
          const sectionName = (pageData.title || pageData.data?.name || "").toLowerCase();
          const isConsentSection = sectionName.includes("consentimiento");

          if (isConsentSection) {
            // Todas las opciones son obligatorias en consentimientos
            const selected = Array.isArray(value) ? value : [];
            const allChecked = field.options.every((opt) => selected.includes(opt));
            if (!allChecked) return true;
          } else {
            const mandatoryOptions = field.options.filter((opt) =>
              opt.toLowerCase().includes("obligatorio")
            );
            if (mandatoryOptions.length > 0) {
              const selected = Array.isArray(value) ? value : [];
              const allChecked = mandatoryOptions.every((opt) => selected.includes(opt));
              if (!allChecked) return true;
            }
          }
        }

        if (!field.required) return false;
        return isEmptyValue(value);
      });
      if (missingCustom) return false;
    }

    return true;
  };

  const handleNext = () => {
    const pageData = pages[currentPage];

    // Página de políticas (siempre es la primera): validar aceptación.
    if (currentPage === 0) {
      if (!isPolicyAccepted(pageData)) {
        setPolicyError("Debes aceptar las políticas de tratamiento de datos para continuar.");
        return;
      }
    } else {
      // Resto de secciones: validar campos obligatorios.
      if (!validateRequiredFields(pageData)) {
        setPolicyError("Por favor completa los campos obligatorios para continuar.");
        return;
      }
    }
    setPolicyError("");

    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
      window.scrollTo(0, 0);
    }
  };

  // --- LOADING ---
  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor={colors.background}
      >
        <CircularProgress size={60} sx={{ color: colors.primary }} />
        <Typography variant="h6" mt={2} color={colors.secondary}>
          Cargando...
        </Typography>
      </Box>
    );
  }

  // --- ERROR ---
  if (error && !eventInfo) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor={colors.background}
        p={3}
      >
        <Paper elevation={4} sx={{ maxWidth: 600, width: "100%", p: 4, borderRadius: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Paper>
      </Box>
    );
  }

  // --- FORMULARIO CERRADO ---
  if (isFormClosed && isFormClosed()) {
    const closureInfo = getFormClosureInfo ? getFormClosureInfo() : {};
    return (
      <Box minHeight="100vh" bgcolor="#ffffff" display="flex" flexDirection="column">
        {/* Header morado */}
        <Box
          sx={{
            bgcolor: colors.purple,
            px: { xs: 2, md: 5 },
            py: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              component="img"
              src="/images/logo_blaco.webp"
              alt="IU Digital"
              sx={{ height: 95, width: "auto", display: "block" }}
            />
          </Box>
        </Box>

        {/* Contenido con imagen de fondo */}
        <Box
          sx={{
            position: "relative",
            flex: 1,
            display: "flex",
            // En móvil el texto se alinea arriba (la imagen ocupa la parte
            // inferior); en PC va centrado verticalmente.
            alignItems: { xs: "flex-start", md: "center" },
            overflow: "hidden",
          }}
        >
          {/* Imagen de fondo principal: pegada al borde inferior derecho.
              En móvil se muestra con opacidad completa (con su degradado) y
              anclada abajo-derecha, sin quedar opaca. */}
          <Box
            component="img"
            src="/images/Frame 436 (1).webp"
            alt=""
            aria-hidden="true"
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              width: { xs: "100%", lg: "85%" },
              height: "auto",
              maxHeight: { xs: "55%", lg: "100%" },
              objectFit: "contain",
              objectPosition: "right bottom",
              zIndex: 0,
              pointerEvents: "none",
              opacity: 1,
            }}
          />

          {/* Contenido central */}
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              width: { xs: "90%", md: "45%" },
              maxWidth: 560,
              textAlign: "left",
              px: { xs: 3, md: 5 },
              py: { xs: 6, md: 0 },
              ml: { xs: "auto", md: 8 },
              mr: { xs: "auto", md: 0 },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "1.8rem", md: "2.4rem" },
                fontWeight: 700,
                color: colors.purple,
                lineHeight: 1.2,
                mb: 2,
              }}
            >
              {closureInfo.title || "Formulario Inactivo"}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "0.95rem", md: "1.1rem" },
                color: "#555",
                lineHeight: 1.6,
              }}
            >
              {closureInfo.message || "El formulario está desactivado en este momento."}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  // --- SIN CUPOS ---
  if (hasAvailableSlots && !hasAvailableSlots()) {
    return (
      <Box minHeight="100vh" bgcolor="#ffffff" display="flex" flexDirection="column">
        {/* Header morado */}
        <Box
          sx={{
            bgcolor: colors.purple,
            px: { xs: 2, md: 5 },
            py: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              component="img"
              src="/images/logo_blaco.webp"
              alt="IU Digital"
              sx={{ height: 95, width: "auto", display: "block" }}
            />
          </Box>
        </Box>

        {/* Contenido con imagen de fondo */}
        <Box
          sx={{
            position: "relative",
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {/* Imagen de sombra de fondo */}
          <Box
            component="img"
            src="/images/Rectangle 78.svg"
            alt=""
            aria-hidden="true"
            sx={{
              position: "absolute",
              bottom: "15%",
              right: "5%",
              width: { xs: "70%", lg: "50%" },
              height: "auto",
              zIndex: 0,
              pointerEvents: "none",
              display: { xs: "none", lg: "block" },
            }}
          />

          {/* Imagen de fondo principal */}
          <Box
            component="img"
            src="/images/Frame 436 (1).webp"
            alt=""
            aria-hidden="true"
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: { xs: "100%", lg: "55%" },
              height: "100%",
              objectFit: "cover",
              objectPosition: "left center",
              zIndex: 0,
              pointerEvents: "none",
              opacity: { xs: 0.15, lg: 1 },
            }}
          />

          {/* Contenido central */}
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              width: { xs: "90%", md: "50%" },
              maxWidth: 560,
              textAlign: { xs: "center", md: "left" },
              px: { xs: 2, md: 5 },
              py: { xs: 6, md: 0 },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "1.8rem", md: "2.4rem" },
                fontWeight: 700,
                color: colors.primary,
                lineHeight: 1.2,
                mb: 2,
              }}
            >
              Cupos Agotados
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "0.95rem", md: "1.1rem" },
                color: "#555",
                lineHeight: 1.6,
              }}
            >
              Se ha alcanzado el límite máximo de registros para este evento.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  // Nota: el registro exitoso (success) se muestra como la sección final de
  // "Confirmación" dentro de renderSectionsPage, con el diseño de pantalla completa.

  // --- FORMULARIO MULTI-PÁGINA ---
  const currentPageData = pages[currentPage];
  const isLastPage = currentPage === totalPages - 1;

  // Detecta si la sección actual es la de "Revisión y envío" (viene de la API,
  // normalmente sin campos). Se le da el diseño especial con botones Editar/Enviar.
  const normalizeText = (str) =>
    (str || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  const isReviewSection =
    currentPageData?.type === "section" &&
    (normalizeText(currentPageData.title).includes("revision") ||
      normalizeText(currentPageData.title).includes("revisi"));

  // Campos base del formulario (nombres, apellidos, documento, correo, tipo de sangre).
  // Recibe opcionalmente el campo crudo "Tipo de documento" (custom) para
  // colocarlo JUNTO a "Número de Identificación" (misma fila en PC). En móvil,
  // "Tipo de documento" va primero (order) por consistencia.
  const renderBaseFields = (documentTypeSourceField = null) => (
    <>
      <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 1 } }}>
        <TextField
          fullWidth
          required
          label="Nombres"
          name="name"
          value={formData.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          disabled={submitting}
          error={!!errors.name}
          helperText={errors.name}
          sx={fieldStyles}
        />
      </Grid>
      <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 2 } }}>
        <TextField
          fullWidth
          required
          label="Apellidos"
          name="lastname"
          value={formData.lastname}
          onChange={(e) => handleInputChange("lastname", e.target.value)}
          disabled={submitting}
          error={!!errors.lastname}
          helperText={errors.lastname}
          sx={fieldStyles}
        />
      </Grid>
      {/* Orden deseado -> Móvil (1 col): Nombres, Apellidos, Tipo de documento,
          Número, Correo, Tipo de Sangre. PC (2 cols): fila [Número | Tipo de
          documento]. Se logra con "order": Tipo doc (xs 3 / md 4), Número
          (xs 4 / md 3). Si no hay campo de documento, Número mantiene su sitio. */}
      {documentTypeSourceField &&
        renderCustomFieldInput(documentTypeSourceField, false, false, {
          order: { xs: 3, md: 4 },
        })}
      <Grid item xs={12} md={6} sx={{ order: { xs: 4, md: 3 } }}>
        <TextField
          fullWidth
          required
          label="Número de Identificación"
          name="number_id"
          value={formData.number_id}
          onChange={(e) => handleInputChange("number_id", e.target.value)}
          disabled={submitting}
          error={!!errors.number_id}
          helperText={errors.number_id}
          sx={fieldStyles}
        />
      </Grid>
      <Grid item xs={12} md={6} sx={{ order: { xs: 5, md: 5 } }}>
        <TextField
          fullWidth
          required
          label="Correo Electrónico"
          name="email"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
          disabled={submitting}
          error={!!errors.email}
          helperText={errors.email}
          sx={fieldStyles}
        />
      </Grid>
      <Grid item xs={12} md={6} sx={{ order: { xs: 6, md: 6 } }}>
        <TextField
          fullWidth
          select
          required
          label="Tipo de Sangre"
          name="blood_type"
          value={formData.blood_type}
          onChange={(e) => handleInputChange("blood_type", e.target.value)}
          disabled={submitting}
          error={!!errors.blood_type}
          helperText={errors.blood_type}
          sx={fieldStyles}
        >
          <MenuItem value="">
            <em>Seleccionar...</em>
          </MenuItem>
          {bloodTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    </>
  );

  const renderPageContent = () => {
    if (!currentPageData) return null;

    if (currentPageData.type === "section") {
      const section = currentPageData.data;
      const sectionFields = section.fields || [];

      // Cuando la sección incluye los campos base, se extrae el campo
      // "Tipo de documento" (custom) para colocarlo junto a "Número de
      // Identificación" (por consistencia). El resto de campos se renderizan
      // en su orden original, sin ese campo.
      const isDocumentType = (f) =>
        normalizeText(f?.label || f?.name || "").includes("tipo de documento");
      const documentTypeSourceField = currentPageData.includeBaseFields
        ? sectionFields.find(isDocumentType)
        : null;
      const remainingFields = documentTypeSourceField
        ? sectionFields.filter((f) => f !== documentTypeSourceField)
        : sectionFields;

      return (
        <Grid container spacing={3}>
          {/* La primera sección incluye además los campos base del formulario */}
          {currentPageData.includeBaseFields && renderBaseFields(documentTypeSourceField)}
          {/* Los campos restantes van DESPUÉS de los base. Como los base usan
              "order" 1-6, aquí se asigna order incremental desde 7 para que no
              se intercalen antes (el default order:0 los adelantaba). */}
          {remainingFields.map((field, i) =>
            currentPageData.includeBaseFields
              ? renderCustomFieldInput(field, false, false, { order: 7 + i })
              : renderCustomFieldInput(field)
          )}
        </Grid>
      );
    }

    if (currentPageData.type === "base_fields") {
      return (
        <Grid container spacing={3}>
          {renderBaseFields()}
        </Grid>
      );
    }

    if (currentPageData.type === "loose_fields") {
      return (
        <Grid container spacing={3}>
          {currentPageData.data.map((field) => renderCustomFieldInput(field))}
        </Grid>
      );
    }

    return null;
  };

  // Pasos del stepper: todas las páginas excepto la de políticas (índice 0).
  const stepPages = pages.slice(1);
  const currentStepIndex = currentPage - 1; // índice dentro de stepPages

  // Configuración visual por sección (imágenes de fondo y título de la izquierda).
  // Cada entrada corresponde al índice de la sección dentro de stepPages.
  const sectionThemes = [
    {
      title: "Iniciar registro",
      // Sección 1: una sola imagen de fondo.
      background: "/images/Frame 436.webp",
      // Imagen específica para móvil (< md). Tablet y PC no la usan.
      mobileImage: "/images/responsive/Frame 438.webp",
      overlay: null,
      formWidth: 6,
      // Mismo esquema que la Sección 5: el fondo (degradado + persona) va
      // full-bleed con objectFit "contain" (rama sin bgRight), de modo que al
      // hacer zoom la imagen completa siempre se ve y NO se recorta ni el
      // degradado ni la persona.
      shadowBottom: "22%",
      shadowRight: "5%",
    },
    {
      title: "Continuación",
      // Sección 2: degradado de fondo + fotografía por encima.
      background: "/images/fondo formas degrade paso2 1.webp",
      mobileImage: "/images/responsive/Frame 439.webp",
      overlay: "/images/INNOVACIÓN VERTICAL 2 1 (1).webp",
      // formWidth 4 (~33%) en vez de 5: el formulario (lista de mesas) deja más
      // espacio a la derecha para que su texto no se solape ni tape la foto.
      formWidth: 4,
      shadowTop: null,
      shadowBottom: "2%",
    },
    {
      title: "Continuación",
      // Sección 3: fondo de ondas, sin imagen superpuesta.
      background: "/images/fondo formas degrade paso2 1 (1).webp",
      mobileImage: "/images/responsive/Frame 440.webp",
      overlay: null,
      // Layout de 3 columnas a ancho completo, con títulos y separadores.
      threeCols: true,
      columnTitles: [
        "Inclusión y diversidad",
        "Pertenencia étnica",
        "Diversidad sexual y de género",
      ],
      shadowImage: "/images/Group.svg",
    },
    {
      title: "Continuación",
      // Sección 4: degradado de fondo + fotografía por encima, opciones apiladas.
      background: "/images/fondo formas degrade paso 4-10 1.svg",
      mobileImage: "/images/responsive/Frame 441.webp",
      overlay: "/images/Rectangle 79.webp",
      shadowBottom: "5%",
      shadowBottomImage: "/images/Group (1).svg",
    },
    {
      title: "Continuación",
      // Sección de revisión y envío (sin campos).
      // Mismo esquema que las secciones 2 y 4: el degradado va a pantalla
      // completa (background, contain full-bleed) para que el fondo nunca sea
      // blanco, y la foto va por encima (overlay).
      background: "/images/fondo formas degrade paso 4-09 1.svg",
      mobileImage: "/images/responsive/Frame 442.webp",
      overlay: "/images/Rectangle 80.webp",
      // La foto de la persona se muestra completa (objectFit "contain"). Para que
      // se vea más grande sin recortarse, se amplía el ancho de su contenedor.
      overlayWidth: { xs: "72%", lg: "58%" },
      // Se baja la foto un poco (aire arriba).
      overlayTop: { lg: "6%" },
      // El texto/botones del formulario se pintan por encima de la foto en la
      // banda donde se solapan (izquierda de la mano). Se limita la columna del
      // formulario (formWidth 5 ~= 41.6%) para que su contenido no invada la
      // zona de la mano/dedos de la persona y no los tape.
      formWidth: 5,
      review: true,
      // Sombra de fondo (Rectangle 78.svg) subida un 5% respecto al borde inferior
      // (de 20% a 25%) para que se vea mejor detrás de la persona.
      shadowBottom: "25%",
      shadowRight: "5%",
    },
    {
      // Sección de confirmación (se muestra tras el registro exitoso).
      // "Frame 436 (1).svg" es una escena ancha (1414x740). Con objectFit
      // "contain" dejaba una franja blanca arriba que cortaba la imagen. Se
      // usa "cover" para llenar el área sin ese recuadro blanco.
      title: "Finalizado",
      background: "/images/Frame 436 (1).webp",
      mobileImage: "/images/responsive/Frame 443.webp",
      // Misma escena y tratamiento que la Sección 1 (Frame 436.svg): la imagen
      // se confina abajo-derecha (bgRight, 70%) con objectFit "contain". Así el
      // área transparente/blanca de la escena queda fuera de la zona del
      // formulario y no aparece como un recuadro que corta a la persona.
      bgRight: true,
      formWidth: 6,
      overlay: null,
      confirmation: true,
      // Sombra (Rectangle 78.svg) bajada un 5% en PC (de 22% a 17% de bottom).
      shadowBottom: "17%",
      shadowRight: "5%",
    },
  ];

  // Tema de la sección actual. Tras el registro exitoso se usa el tema de
  // confirmación; para la revisión, su tema dedicado (review: true).
  const reviewTheme = sectionThemes.find((t) => t.review);
  const confirmationTheme = sectionThemes.find((t) => t.confirmation);
  const fallbackTheme = sectionThemes[sectionThemes.length - 1] || sectionThemes[0];
  const stepTheme = sectionThemes[currentStepIndex] || fallbackTheme;
  const currentTheme = success
    ? confirmationTheme || fallbackTheme
    : isReviewSection
    ? reviewTheme || fallbackTheme
    : stepTheme;

  // Botón principal (Siguiente / Enviar) reutilizable en los distintos layouts.
  const renderSubmitButton = () => (
    <Button
      type="submit"
      variant="contained"
      color="inherit"
      disableRipple
      disableElevation
      disabled={submitting}
      startIcon={isLastPage && submitting ? <CircularProgress size={18} color="inherit" /> : null}
      sx={{
        backgroundColor: "#C2383A !important",
        color: "#ffffff !important",
        boxShadow: "none !important",
        "&:hover": { backgroundColor: "#a12224 !important", color: "#ffffff !important" },
        "&:active": { backgroundColor: "#C2383A !important", color: "#ffffff !important" },
        "&:focus": { backgroundColor: "#C2383A !important", color: "#ffffff !important" },
        "&.Mui-focusVisible": {
          backgroundColor: "#C2383A !important",
          color: "#ffffff !important",
        },
        minWidth: "160px",
        height: "44px",
        borderRadius: "8px",
        px: "28px",
        fontFamily: "'Metropolis', sans-serif",
        fontWeight: 600,
        fontStyle: "normal",
        fontSize: "20px",
        lineHeight: "30px",
        letterSpacing: "-0.02em",
        textAlign: "center",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {/* eslint-disable-next-line prettier/prettier */}
      {submitting
        ? "Registrando..."
        : isReviewSection
        ? "Enviar registro"
        : isLastPage
        ? "Enviar"
        : "Siguiente"}
    </Button>
  );

  // Render de sección a 3 columnas (ancho completo), con títulos y separadores.
  // Los campos se agrupan de a 2 por columna; el botón va en la última columna.
  const renderThreeColSection = () => {
    const sectionFields = currentPageData?.data?.fields || [];
    const titles = currentTheme.columnTitles || [];
    const groups = [];
    for (let i = 0; i < sectionFields.length; i += 2) {
      groups.push(sectionFields.slice(i, i + 2));
    }

    return (
      <Grid container>
        {groups.map((group, gIdx) => (
          <Grid
            item
            xs={12}
            md={12 / groups.length}
            key={gIdx}
            sx={{
              // La primera columna sin padding-izquierdo para que su contenido
              // quede alineado con el título "Continuación" (a la izquierda).
              // Las demás mantienen padding a ambos lados.
              pl: { xs: 0, md: gIdx === 0 ? 0 : 3 },
              pr: { xs: 0, md: 3 },
              // Línea delgada divisoria entre columnas (excepto la primera).
              borderLeft: { md: gIdx > 0 ? "1px solid #e0e0e0" : "none" },
            }}
          >
            {/* Título de la columna. El de la primera columna es el subtítulo
                que va debajo de "Continuación": 20px como en las demás secciones. */}
            <Typography
              sx={{
                fontSize: gIdx === 0 ? "16px" : "0.9rem",
                color: colors.purple,
                mb: 3,
                minHeight: 20,
              }}
            >
              {gIdx === 0 && (
                <Box component="span" sx={sectionTitleStyles}>
                  {getSectionTitle(currentPageData)}
                </Box>
              )}
              {gIdx !== 0 && titles[gIdx]}
            </Typography>

            <Grid container spacing={2}>
              {group.map((field) => renderCustomFieldInput(field, true, true))}
            </Grid>

            {/* El botón va dentro de la última columna */}
            {gIdx === groups.length - 1 && (
              <Box sx={{ mt: { xs: 4, md: 20 } }} display="flex" justifyContent="flex-start">
                {renderSubmitButton()}
              </Box>
            )}
          </Grid>
        ))}
      </Grid>
    );
  };

  // --- NUEVA PÁGINA: FORMULARIO POR SECCIONES (diseño pantalla completa) ---
  const renderSectionsPage = () => (
    <Box minHeight="100vh" bgcolor="#ffffff" display="flex" flexDirection="column">
      {/* Header morado */}
      <Box
        sx={{
          bgcolor: colors.purple,
          px: { xs: 2, md: 5 },
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            component="img"
            src="/images/logo_blaco.webp"
            alt="IU Digital"
            sx={{ height: 95, width: "auto", display: "block" }}
          />
        </Box>

        <Button
          onClick={() => {
            if (success) {
              handleGoToStart();
            } else {
              handleBack();
            }
          }}
          disableRipple
          startIcon={<ArrowBackIcon sx={{ fontSize: "16px !important" }} />}
          sx={{
            color: "#ffffff !important",
            textTransform: "uppercase",
            fontSize: "0.875rem",
            fontWeight: 500,
            letterSpacing: "0.5px",
            "&:hover": { bgcolor: "transparent", color: "#ffffff !important" },
            "&:active": { bgcolor: "transparent", color: "#ffffff !important" },
            "&:focus": { bgcolor: "transparent", color: "#ffffff !important" },
            "&:visited": { color: "#ffffff !important" },
            "&.Mui-focusVisible": { bgcolor: "transparent", color: "#ffffff !important" },
          }}
        >
          <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
            {success || currentStepIndex === 0
              ? "Regresar al inicio"
              : "Regresar a la sección anterior"}
          </Box>
          <Box component="span" sx={{ display: { xs: "inline", md: "none" } }}>
            Regresar
          </Box>
        </Button>
      </Box>

      {/* Contenido: imagen(es) de fondo por debajo, formulario por encima */}
      <Box
        sx={{
          position: "relative",
          flex: 1,
          display: { xs: "flex", lg: "block" },
          flexDirection: "column",
          height: { lg: "calc(100vh - 64px)" },
          overflow: { lg: "hidden" },
        }}
      >
        {/* Sombra de fondo (se muestra en todas las secciones, debajo de las imágenes) */}
        <Box
          component="img"
          src={currentTheme.shadowImage || "/images/Rectangle 78.svg"}
          alt=""
          aria-hidden="true"
          sx={{
            position: "absolute",
            top: currentTheme.shadowTop || "auto",
            bottom: currentTheme.shadowBottom
              ? currentTheme.shadowBottom
              : currentTheme.shadowTop
              ? "auto"
              : 0,
            right: currentTheme.shadowRight || 0,
            width: { xs: "70%", lg: "50%" },
            height: "auto",
            zIndex: 0,
            pointerEvents: "none",
            display: { xs: "none", lg: "block" },
          }}
        />
        {/* Segunda imagen de fondo pegada al borde inferior (solo cuando aplica) */}
        {currentTheme.shadowBottomImage && (
          <Box
            component="img"
            src={currentTheme.shadowBottomImage}
            alt=""
            aria-hidden="true"
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              width: { xs: "70%", lg: "50%" },
              height: "auto",
              zIndex: 0,
              pointerEvents: "none",
              display: { xs: "none", lg: "block" },
            }}
          />
        )}
        {/* Capa de fondo (degradado o imagen principal). Cambia según la sección.
            En móvil y tablet se oculta aquí y se muestra al final del contenido. */}
        <Fade in key={`bg-${currentStepIndex}`} timeout={450}>
          <Box
            component="img"
            src={currentTheme.background}
            alt=""
            aria-hidden="true"
            sx={{
              position: "absolute",
              ...(currentTheme.bgRight
                ? {
                    // Caja con alto y ancho definidos + objectFit "contain":
                    // la imagen (degradado + persona) se ajusta completa dentro
                    // de la caja y NUNCA se recorta, ni por abajo ni por los
                    // lados. Antes usaba height "auto", que dejaba que el alto
                    // creciera más que el contenedor y recortaba la parte baja.
                    top: 0,
                    bottom: 0,
                    right: 0,
                    width: "70%",
                    height: "100%",
                    objectFit: "contain",
                    objectPosition: "right bottom",
                  }
                : {
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    objectPosition: "right bottom",
                  }),
              zIndex: 1,
              pointerEvents: "none",
              display: { xs: "none", lg: "block" },
            }}
          />
        </Fade>
        {/* Capa superior (fotografía) por encima del degradado, cuando aplica.
            Se limita a la mitad derecha para que no domine la pantalla.
            En móvil y tablet se oculta aquí y se muestra al final. */}
        {currentTheme.overlay && (
          <Fade in key={`ov-${currentStepIndex}`} timeout={550}>
            <Box
              component="img"
              src={currentTheme.overlay}
              alt=""
              aria-hidden="true"
              sx={{
                position: "absolute",
                // "overlayTop" empuja la foto hacia abajo dejando aire arriba,
                // para que las manos/dedos no queden recortados contra el borde
                // superior (con objectFit "contain" la imagen sigue completa).
                top: currentTheme.overlayTop || 0,
                bottom: 0,
                right: 0,
                width: currentTheme.overlayWidth || { xs: "60%", lg: "45%" },
                height: "100%",
                objectFit: currentTheme.overlayFit || "contain",
                objectPosition: "right bottom",
                zIndex: 1,
                pointerEvents: "none",
                display: { xs: "none", lg: "block" },
              }}
            />
          </Fade>
        )}
        {/* Stepper de secciones (ancho completo) */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            px: { xs: 2, md: 6 },
            pt: { xs: 2, md: 5 },
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: `repeat(3, 1fr)`,
                md: `repeat(${stepPages.length}, 1fr)`,
              },
              gap: 0,
              rowGap: { xs: 2, md: 0 },
              mb: 4,
            }}
          >
            {stepPages.map((step, index) => {
              // Tras el registro exitoso, todos los pasos se marcan completados.
              const isActive = !success && index === currentStepIndex;
              const isDone = success || index < currentStepIndex;
              return (
                <Box
                  key={index}
                  sx={{
                    minWidth: 0,
                    pl: index % 3 === 0 ? 0 : 1,
                    pr: 1,
                  }}
                >
                  {/* Línea sobre el texto */}
                  <Box
                    sx={{
                      height: "2px",
                      bgcolor: isActive || isDone ? colors.purple : "#d9d9d9",
                      mb: 1,
                      transition: "background-color 0.3s ease",
                    }}
                  />
                  <Box display="flex" alignItems="center" sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        border: `2px solid ${isActive || isDone ? colors.purple : "#c9c9c9"}`,
                        bgcolor: isDone ? colors.purple : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        mr: 0.5,
                        transition: "all 0.3s ease",
                      }}
                    >
                      {isDone && <CheckIcon sx={{ fontSize: 16, color: "#ffffff" }} />}
                      {isActive && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: colors.purple,
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      noWrap
                      sx={{
                        fontFamily: "'Roboto Condensed', sans-serif",
                        fontWeight: 400,
                        fontStyle: "normal",
                        fontSize: { xs: "14px", md: "16px" },
                        lineHeight: "19px",
                        letterSpacing: "-0.02em",
                        color: isActive ? colors.purple : "#8a8a8a",
                      }}
                    >
                      <Box component="span" sx={{ display: { xs: "inline", md: "none" } }}>
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          {`Sección ${index + 1}`}
                        </Box>
                      </Box>
                      <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          {`Sección ${index + 1}: `}
                        </Box>
                        {step.title}
                      </Box>
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Grid
          container
          sx={{
            position: "relative",
            zIndex: 1,
            pointerEvents: { xs: "none", lg: "auto" },
            flex: { xs: 1, lg: "unset" },
          }}
        >
          {/* Columna del formulario */}
          <Grid
            item
            xs={12}
            lg={currentTheme.threeCols ? 12 : currentTheme.formWidth || 7}
            sx={{
              px: { xs: 2, md: 6 },
              pb: { xs: 2, md: 5 },
              // Transparente desde md hacia arriba: en pantallas medianas/PC el
              // fondo blanco de la columna cortaba la imagen de fondo (se veía un
              // bloque blanco recto sobre el degradado). Solo en móvil (xs) se
              // mantiene blanco para legibilidad, ya que ahí la imagen va debajo.
              bgcolor: { xs: "#ffffff", md: "transparent" },
              pointerEvents: "auto",
            }}
          >
            {/* Título */}
            <Typography
              sx={{
                fontSize: "28px",
                fontWeight: 700,
                color: colors.purple,
                fontFamily: "'Metropolis', sans-serif",
                mb: 0.5,
              }}
            >
              {currentTheme.title}
            </Typography>
            <Box sx={{ width: 190, borderTop: `2px solid ${colors.purple}`, mb: 2 }} />

            {/* Contenido de la sección con transición al cambiar de página */}
            <Fade in key={currentPage} timeout={450}>
              <Box>
                {/* Subtítulo de sección (en 3 columnas, revisión y confirmación
                    el subtítulo va dentro del contenido) */}
                {!currentTheme.threeCols && !isReviewSection && !success && (
                  <Typography sx={{ ...sectionTitleStyles, color: colors.purple, mb: 3 }}>
                    {getSectionTitle(currentPageData)}
                  </Typography>
                )}

                {error && (
                  <Alert severity="error" onClose={clearError} sx={{ mb: 2, fontSize: "0.875rem" }}>
                    {error}
                  </Alert>
                )}

                {/* Formulario */}
                <Box
                  component="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    // En la sección de revisión se envía; en la última página también.
                    if (isReviewSection || isLastPage) {
                      handleSubmit(e);
                    } else {
                      handleNext();
                    }
                  }}
                  sx={{ maxWidth: currentTheme.threeCols ? "100%" : 620 }}
                >
                  {success ? (
                    <>
                      <Typography sx={{ fontSize: "0.9rem", color: colors.purple, mb: 3 }}>
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          Sección {stepPages.length}:
                        </Box>{" "}
                        Confirmación
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "'Roboto Condensed', sans-serif",
                          fontWeight: 300,
                          fontStyle: "italic",
                          fontSize: "20px",
                          lineHeight: "30px",
                          letterSpacing: "-0.02em",
                          color: "#333",
                          mb: 4,
                          maxWidth: 620,
                        }}
                      >
                        Gracias por registrarte. Te damos la bienvenida al evento, en los próximos
                        días recibirás un correo electrónico con la confirmación de tu registro, tus
                        credenciales de acceso y todos los detalles del evento.
                      </Typography>
                      <Button
                        type="button"
                        variant="contained"
                        color="inherit"
                        disableRipple
                        disableElevation
                        onClick={handleGoToStart}
                        sx={{
                          backgroundColor: "#C2383A !important",
                          color: "#ffffff !important",
                          boxShadow: "none !important",
                          "&:hover": {
                            backgroundColor: "#a12224 !important",
                            color: "#ffffff !important",
                          },
                          height: "44px",
                          borderRadius: "8px",
                          px: "28px",
                          textTransform: "uppercase",
                          fontSize: "0.875rem",
                          fontFamily: "'Metropolis', sans-serif",
                          fontWeight: 700,
                          letterSpacing: "0.5px",
                        }}
                      >
                        Volver a la página de inicio
                      </Button>
                    </>
                  ) : isReviewSection ? (
                    <>
                      <Typography sx={{ ...sectionTitleStyles, color: colors.purple, mb: 3 }}>
                        {getSectionTitle(currentPageData)}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "'Roboto Condensed', sans-serif",
                          fontWeight: 300,
                          fontSize: "20px",
                          lineHeight: "30px",
                          letterSpacing: "-0.02em",
                          color: "#333",
                          mb: 4,
                        }}
                      >
                        Revisa los datos diligenciados antes de enviar. Puede editar cualquier
                        sección.
                      </Typography>
                      <Box display="flex" gap={2} alignItems="center">
                        <Button
                          type="button"
                          variant="outlined"
                          onClick={handleBack}
                          disableRipple
                          sx={{
                            color: colors.purple,
                            borderColor: "#c9c9c9",
                            "&:hover": { borderColor: colors.purple, bgcolor: "transparent" },
                            minWidth: "120px",
                            height: "44px",
                            borderRadius: "8px",
                            px: "24px",
                            textTransform: "uppercase",
                            fontSize: "0.875rem",
                            fontFamily: "'Metropolis', sans-serif",
                            fontWeight: 700,
                            letterSpacing: "0.5px",
                          }}
                        >
                          Editar
                        </Button>
                        {renderSubmitButton()}
                      </Box>
                    </>
                  ) : currentTheme.threeCols ? (
                    renderThreeColSection()
                  ) : (
                    renderPageContent()
                  )}

                  {policyError && (
                    <Alert severity="warning" sx={{ mt: 2, fontSize: "0.875rem" }}>
                      {policyError}
                    </Alert>
                  )}

                  {/* Botón genérico (no aplica en 3 columnas, revisión ni confirmación,
                      que tienen su propia disposición de botones) */}
                  {!currentTheme.threeCols && !isReviewSection && !success && (
                    <Box sx={{ mt: { xs: 2, md: 4 } }} display="flex" gap={2} alignItems="center">
                      {renderSubmitButton()}
                    </Box>
                  )}
                </Box>
              </Box>
            </Fade>
          </Grid>
        </Grid>

        {/* MÓVIL (< md): una sola imagen específica de /images/responsive por
            sección. Va en flujo (height auto, width 100%), así el contenedor
            toma su altura real: sin espacio en blanco arriba ni scroll. Tablet
            y PC NO usan este bloque. */}
        {isMobile && currentTheme.mobileImage && (
          <Box
            sx={{
              display: { xs: "block", md: "none" },
              width: "100%",
              // Se acerca la imagen al botón con margen negativo para compensar
              // el padding inferior de la columna del formulario en móvil.
              mt: -3,
              lineHeight: 0,
            }}
          >
            <Box
              component="img"
              src={currentTheme.mobileImage}
              alt=""
              aria-hidden="true"
              sx={{
                display: "block",
                width: "100%",
                height: "auto",
              }}
            />
          </Box>
        )}

        {/* TABLET (md a lg): mismas imágenes de escritorio (fondo + overlay +
            sombra). Antes el bloque tenía flex:1 (heredado a md), que competía
            con el flex del formulario y comprimía la imagen en una banda corta,
            haciendo que objectFit "contain" la escalara pequeña y pareciera
            recortada. Ahora: flex "none" (no se estira) y la imagen de fondo va
            en FLUJO natural (width 100%, height auto) para que el contenedor tome
            su altura real y la imagen (degradado + persona) se vea completa. */}
        <Box
          sx={{
            display: { xs: "none", md: "block", lg: "none" },
            width: "100%",
            position: "relative",
            mt: "auto",
            flex: "none",
          }}
        >
          {/* Sombra debajo de las imágenes */}
          <Box
            component="img"
            src={currentTheme.shadowImage || "/images/Rectangle 78.svg"}
            alt=""
            aria-hidden="true"
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              width: "100%",
              height: "auto",
              maxHeight: "100%",
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
          <Box
            component="img"
            src={currentTheme.background}
            alt=""
            aria-hidden="true"
            sx={{
              display: "block",
              width: "100%",
              height: "auto",
              position: "relative",
              zIndex: 1,
            }}
          />
          {currentTheme.overlay && (
            <Box
              component="img"
              src={currentTheme.overlay}
              alt=""
              aria-hidden="true"
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                objectPosition: "right bottom",
                zIndex: 2,
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );

  // Si no estamos en la página de políticas (índice 0), o el registro fue
  // exitoso (pantalla de confirmación), mostrar el nuevo diseño.
  if (currentPage > 0 || success) {
    return renderSectionsPage();
  }

  return (
    <Box minHeight="90vh" bgcolor={colors.background}>
      {/* Banner superior */}
      <Box
        sx={{
          width: "100%",
          overflow: "hidden",
        }}
      >
        <img
          src="/images/banner-evento.webp"
          alt="Banner del evento"
          style={{
            width: "100%",
            height: "auto",
            display: "block",
          }}
        />
      </Box>

      {/* Línea separadora */}

      {/* Contenido principal.
          El banner superior ocupa el 100% del ancho y el "2" queda pegado al
          borde izquierdo (con un pequeño padding). En PC grandes y extragrandes
          el contenido debe alinear su borde izquierdo con ese "2", NO centrarse.
          Por eso a partir de lg el contenedor se alinea a la izquierda
          (mr: auto) y se limita el ancho para que no se estire de lado a lado.
          En tablets/celulares/PC pequeños (< lg) se mantiene el comportamiento
          normal de maxWidth "lg". */}
      <Box
        sx={{
          py: { xs: 3, md: 5 },
          // El padding izquierdo alinea el contenido con el "2" del banner
          // (que arranca casi pegado al borde izquierdo de la imagen full-width).
          // En PC grandes se pega a la izquierda; en pantallas pequeñas se centra.
          pr: { xs: 2, md: 3 },
          pl: { xs: 2, md: 3, lg: "7%" },
          // Ancho controlado: pegado a la izquierda en lg+ (no centrado) para
          // seguir al "2". El contenido se distribuye más a la derecha
          // ampliando el ancho disponible en PC grandes/extragrandes (usa un %
          // del ancho para acompañar el ancho del banner) sin despegarlo de la
          // izquierda. Por debajo de lg se centra con maxWidth acotado.
          width: "100%",
          maxWidth: { xs: "100%", sm: "600px", md: "900px", lg: "93%", xl: "93%" },
          marginLeft: { xs: "auto", lg: 0 },
          marginRight: { xs: "auto", lg: 0 },
        }}
      >
        <Grid container spacing={{ xs: 3, md: 5 }}>
          {/* Columna izquierda - Info del evento */}
          <Grid item xs={12} md={5}>
            <Typography
              sx={{
                fontSize: { xs: "28px", md: "40px" },
                fontWeight: 700,
                color: colors.purple,
                mb: -1,
                fontFamily: "'Metropolis', sans-serif",
                ml: 0,
              }}
            >
              Formulario de Registro
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "16px", md: "20px" },
                fontWeight: 600,
                color: colors.purple,
                fontFamily: "'Metropolis', sans-serif",
                mb: 2,
              }}
            >
              2º Encuentro Nacional de Centros de Innovación
            </Typography>

            {/* Línea divisoria */}
            <Box sx={{ borderTop: "1px solid #210D39", mb: 3 }} />

            {/* Fecha y Lugar */}
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={{ xs: 1, md: 2 }} mb={3}>
              <Typography
                sx={{
                  fontFamily: "'Roboto Condensed', sans-serif",
                  fontWeight: 300,
                  fontStyle: "normal",
                  fontSize: "16px",
                  lineHeight: "30px",
                  letterSpacing: "-0.02em",
                  color: colors.purple,
                }}
              >
                <Box component="span" sx={{ fontWeight: 700, color: "#210D39" }}>
                  Fecha:
                </Box>{" "}
                <Box component="span" sx={{ color: "#000000" }}>
                  4 y 5 de Noviembre
                </Box>
              </Typography>
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "inline" }, color: "#210D39", mx: 0.5 }}
              >
                |
              </Box>
              <Typography
                sx={{
                  fontFamily: "'Roboto Condensed', sans-serif",
                  fontWeight: 300,
                  fontStyle: "normal",
                  fontSize: "16px",
                  lineHeight: "30px",
                  letterSpacing: "-0.02em",
                  color: colors.purple,
                }}
              >
                <Box component="span" sx={{ fontWeight: 700, color: "#210D39" }}>
                  Lugar:
                </Box>{" "}
                <Box component="span" sx={{ color: "#000000" }}>
                  Institución Universitaria Digital de Antioquia
                </Box>
              </Typography>
            </Box>

            {/* Descripción del evento */}
            {eventInfo?.event_description && (
              <>
                <Typography
                  sx={{
                    fontFamily: "'Roboto Condensed', sans-serif",
                    fontWeight: 400,
                    fontStyle: "normal",
                    fontSize: { xs: "18px", md: "18px" },
                    lineHeight: { xs: "22px", md: "20px" },
                    letterSpacing: "-0.02em",
                    color: "#000000",
                    mb: 1,
                  }}
                >
                  Bienvenido/a al 2º Encuentro Nacional de Centros de Innovación: Construyamos el
                  futuro de la educación digital
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Roboto Condensed', sans-serif",
                    fontWeight: 300,
                    fontStyle: "normal",
                    fontSize: { xs: "17px", md: "17px" },
                    lineHeight: { xs: "20px", md: "20px" },
                    letterSpacing: "-0.02em",
                    color: "#000000",
                  }}
                >
                  Este evento aborda la innovación educativa digital y su papel en la transformación
                  de la educación, promoviendo el acceso, la equidad y nuevas formas de aprendizaje.
                </Typography>
              </>
            )}
          </Grid>

          {/* Columna derecha - Formulario */}
          <Grid item xs={12} md={7}>
            <Paper elevation={0} sx={{ p: { xs: 0, md: 3 }, borderRadius: 1 }}>
              {/* Título de la sección/página actual */}
              {currentPageData?.type === "section" && matchesPolitica(currentPageData.title) ? (
                <>
                  <Typography
                    sx={{
                      fontFamily: "'Roboto Condensed', sans-serif",
                      fontWeight: 100,
                      fontStyle: "normal",
                      fontSize: "22px",
                      lineHeight: "30px",
                      letterSpacing: "-0.02em",
                      color: "#000000",
                      mb: 1.5,
                    }}
                  >
                    Por favor completa este formulario. Tus datos serán tratados conforme a la Ley
                    de Protección de Datos Personales.{" "}
                    <Link
                      href="https://www.iudigital.edu.co/pdfs/transparencia/Normatividad/Resoluciones/2024/Politica%20Proteccion%20de%20Datos.pdf"
                      target="_blank"
                      underline="always"
                      sx={{
                        color: "#C2383A",
                        textDecorationLine: "underline !important",
                        textDecorationColor: "#C2383A !important",
                        textDecorationStyle: "solid !important",
                        "&:link": { textDecorationLine: "underline !important" },
                        "&:visited": { textDecorationLine: "underline !important" },
                        "&:hover": { textDecorationLine: "underline !important" },
                      }}
                    >
                      Política de Datos Personales
                    </Link>
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Roboto Condensed', sans-serif",
                      fontWeight: 300,
                      fontStyle: "normal",
                      fontSize: "18px",
                      lineHeight: "23px",
                      letterSpacing: "-0.02em",
                      color: "#000000",
                      mb: 2,
                    }}
                  >
                    Manifiesto que he consultado en{" "}
                    <Link
                      href="https://www.iudigital.edu.co"
                      target="_blank"
                      underline="always"
                      sx={{
                        fontFamily: "'Roboto Condensed', sans-serif",
                        fontWeight: 300,
                        fontSize: "18px",
                        lineHeight: "23px",
                        letterSpacing: "-0.02em",
                        color: "#C2383A",
                        textDecorationLine: "underline !important",
                        textDecorationColor: "#C2383A !important",
                        textDecorationStyle: "solid !important",
                        "&:link": { textDecorationLine: "underline !important" },
                        "&:visited": { textDecorationLine: "underline !important" },
                        "&:hover": { textDecorationLine: "underline !important" },
                      }}
                    >
                      https://www.iudigital.edu.co
                    </Link>{" "}
                    el Manual de Políticas de Tratamiento de Información y Protección de los Datos
                    Personales y certifico que conozco sobre mis derechos para solicitar la
                    eliminación, rectificación, actualización y supresión de mis datos personales,
                    mediante los canales dispuestos por la universidad.
                  </Typography>
                </>
              ) : (
                <Typography sx={{ ...sectionTitleStyles, color: colors.secondary, mb: 2 }}>
                  {getSectionTitle(currentPageData)}
                </Typography>
              )}

              {error && (
                <Alert severity="error" onClose={clearError} sx={{ mb: 2, fontSize: "0.875rem" }}>
                  {error}
                </Alert>
              )}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (isLastPage) {
                    handleSubmit(e);
                  } else {
                    handleNext();
                  }
                }}
              >
                {renderPageContent()}

                {policyError && (
                  <Alert severity="warning" sx={{ mt: 2, fontSize: "0.875rem" }}>
                    {policyError}
                  </Alert>
                )}

                {/* Navegación */}
                <Box
                  display="flex"
                  justifyContent={currentPage > 0 ? "space-between" : "flex-start"}
                  alignItems="center"
                  mt={3}
                >
                  {currentPage > 0 && (
                    <Button
                      variant="text"
                      onClick={handleBack}
                      startIcon={<ArrowBackIcon />}
                      sx={{ color: colors.secondary, textTransform: "none", fontSize: "0.875rem" }}
                    >
                      Atrás
                    </Button>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    color="inherit"
                    disableRipple
                    disableElevation
                    disabled={submitting}
                    startIcon={
                      isLastPage && submitting ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : null
                    }
                    sx={{
                      backgroundColor: "#C2383A !important",
                      color: "#ffffff !important",
                      boxShadow: "none !important",
                      "&:hover": {
                        backgroundColor: "#a12224 !important",
                        color: "#ffffff !important",
                      },
                      "&:active": {
                        backgroundColor: "#C2383A !important",
                        color: "#ffffff !important",
                      },
                      "&:focus": {
                        backgroundColor: "#C2383A !important",
                        color: "#ffffff !important",
                      },
                      "&:focus-visible": {
                        backgroundColor: "#C2383A !important",
                        color: "#ffffff !important",
                      },
                      "&.Mui-focusVisible": {
                        backgroundColor: "#C2383A !important",
                        color: "#ffffff !important",
                      },
                      "& .MuiTouchRipple-root": { color: "rgba(255,255,255,0.3)" },
                      width: "177px",
                      height: "44px",
                      gap: "10px",
                      borderRadius: "8px",
                      pt: "10px",
                      pr: "28px",
                      pb: "10px",
                      pl: "28px",
                      opacity: 1,
                      fontFamily: "'Metropolis', sans-serif",
                      fontWeight: 600,
                      fontStyle: "normal",
                      fontSize: "20px",
                      lineHeight: "30px",
                      letterSpacing: "-0.02em",
                      textAlign: "center",
                      textTransform: "uppercase",
                    }}
                  >
                    {isLastPage ? (submitting ? "Registrando..." : "ENVIAR") : "ENVIAR"}
                  </Button>
                </Box>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
